import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { BUILTIN_ACTIVITY_TYPE_KEYS } from "@/lib/activitySettingsSchema";
import { BUILTIN_ACTIVITY_TYPE_DEFAULTS } from "@/lib/organizationActivityTypeOptions";
import { queryConfigs } from "@/lib/queryClient";
import {
  isActivityTypesTableMissingForOrg,
  isSupabaseRelationUnavailable,
  markActivityTypesTableMissing,
  markActivityTypesTablePresent,
} from "@/lib/activitySchemaAvailability";

export interface OrganizationActivityTypeRow {
  id: string;
  org_id: string;
  key: string;
  label: string;
  icon: string | null;
  enabled: boolean;
  sort_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

const qk = (orgId: string | null) => ["organization-activity-types", orgId] as const;

/** Referência estável — evita `?? []` no retorno, que gerava novo array a cada render e loops em useEffect. */
const EMPTY_ROWS: OrganizationActivityTypeRow[] = [];

export function useOrganizationActivityTypes() {
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [typesTableUnavailable, setTypesTableUnavailable] = useState(false);

  useEffect(() => {
    setTypesTableUnavailable(false);
  }, [orgId]);

  const query = useQuery({
    // Config estrutural (staleTime 30 min, sem refetch em mount/focus): tabela de
    // config lida em cada modal de criar/editar atividade; antes usava o default
    // CRM (30 s) e refetchava ~94 k vezes. As mutations abaixo invalidam a queryKey,
    // então edições aparecem na hora. `retry` custom preservado abaixo do spread
    // (não pode reter em relação ausente). Ver docs/ai — Chip R2-A (cache de config).
    ...queryConfigs.structural,
    queryKey: qk(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const { data, error } = await supabase
        .from("organization_activity_types")
        .select("*")
        .eq("org_id", orgId)
        .order("sort_order", { ascending: true });
      if (error) {
        if (isSupabaseRelationUnavailable(error)) {
          markActivityTypesTableMissing(orgId);
          setTypesTableUnavailable(true);
          return EMPTY_ROWS;
        }
        throw error;
      }
      markActivityTypesTablePresent(orgId);
      setTypesTableUnavailable(false);
      return (data || []) as OrganizationActivityTypeRow[];
    },
    enabled: !!orgId,
    retry: (failureCount, err) => failureCount < 2 && !isSupabaseRelationUnavailable(err),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk(orgId) });

  const createCustom = useMutation({
    mutationFn: async (input: { label: string; icon?: string | null }) => {
      if (!orgId) throw new Error("Sem organização");
      const key = `custom_${crypto.randomUUID()}`;
      const rows = query.data || [];
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0);
      const { data, error } = await supabase
        .from("organization_activity_types")
        .insert({
          org_id: orgId,
          key,
          label: input.label.trim(),
          icon: input.icon?.trim() || null,
          enabled: true,
          sort_order: maxOrder + 10,
          is_system: false,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as OrganizationActivityTypeRow;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Tipo criado", description: "Novo tipo de atividade adicionado." });
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível criar o tipo.", variant: "destructive" });
    },
  });

  const updateRow = useMutation({
    mutationFn: async (input: Partial<OrganizationActivityTypeRow> & { id: string }) => {
      if (!orgId) throw new Error("Sem organização");
      const { id, ...rest } = input;
      const existing = (query.data || []).find((r) => r.id === id);
      let patch: Record<string, unknown> = { ...rest };
      if (existing?.is_system) {
        patch = {};
        const allowed = ["label", "icon", "enabled", "sort_order"] as const;
        for (const k of allowed) {
          if (k in rest && (rest as Record<string, unknown>)[k] !== undefined) {
            patch[k] = (rest as Record<string, unknown>)[k];
          }
        }
      }
      const { error } = await supabase
        .from("organization_activity_types")
        .update(patch)
        .eq("id", id)
        .eq("org_id", orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível atualizar.", variant: "destructive" });
    },
  });

  const removeCustom = useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error("Sem organização");
      const row = (query.data || []).find((r) => r.id === id);
      if (row?.is_system) throw new Error("Tipo do sistema não pode ser excluído");
      const { error } = await supabase
        .from("organization_activity_types")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
        .eq("is_system", false);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Tipo removido" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível excluir o tipo.", variant: "destructive" });
    },
  });

  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      if (!orgId) throw new Error("Sem organização");
      for (let i = 0; i < orderedIds.length; i++) {
        const { error } = await supabase
          .from("organization_activity_types")
          .update({ sort_order: (i + 1) * 10 })
          .eq("id", orderedIds[i])
          .eq("org_id", orgId);
        if (error) throw error;
      }
    },
    onSuccess: () => invalidate(),
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível reordenar.", variant: "destructive" });
    },
  });

  /** Insere no banco as linhas padrão que faltarem (ex.: org antiga sem seed). Requer papel admin (RLS). */
  const seedMissingBuiltins = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      if (isActivityTypesTableMissingForOrg(orgId)) return;
      const rows =
        (queryClient.getQueryData(qk(orgId)) as OrganizationActivityTypeRow[] | undefined) || [];
      const have = new Set(rows.map((r) => r.key));
      const missing = BUILTIN_ACTIVITY_TYPE_KEYS.filter((k) => !have.has(k));
      if (missing.length === 0) return;
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0);
      const inserts = missing.map((key, i) => {
        const def = BUILTIN_ACTIVITY_TYPE_DEFAULTS[key];
        return {
          org_id: orgId,
          key,
          label: def.label,
          icon: def.iconName,
          enabled: true,
          sort_order: maxOrder + (i + 1) * 10,
          is_system: true,
        };
      });
      const { error } = await supabase.from("organization_activity_types").insert(inserts);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      if (orgId && isSupabaseRelationUnavailable(err)) {
        markActivityTypesTableMissing(orgId);
        setTypesTableUnavailable(true);
        return;
      }
      toast({
        title: "Erro ao sincronizar tipos",
        description: "Verifique permissões de admin ou tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    rows: query.data ?? EMPTY_ROWS,
    isLoading: query.isLoading,
    /** Migração `organization_activity_types` não aplicada no Supabase (404 / tabela ausente). */
    typesTableUnavailable,
    refetch: query.refetch,
    createCustom: createCustom.mutateAsync,
    updateRow: updateRow.mutateAsync,
    removeCustom: removeCustom.mutateAsync,
    reorder: reorder.mutateAsync,
    seedMissingBuiltins: seedMissingBuiltins.mutateAsync,
    isMutating:
      createCustom.isPending ||
      updateRow.isPending ||
      removeCustom.isPending ||
      reorder.isPending ||
      seedMissingBuiltins.isPending,
  };
}
